import { useEffect, useState } from 'react';
import Layout from '../components/layout';
import Title from '../components/title';
import { supabase } from '../supabase';
import './tablePage.css';
import searchIcon from '../assets/search.svg';
import editIcon from '../assets/edit.svg';
import deleteIcon from '../assets/delete.svg';

const CATEGORIES_PER_PAGE = 4;

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const [name, setName] = useState('');
  const [color, setColor] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const [id, setId] = useState('');
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState('');
  const [isEditOpen, setIsEditOpen] = useState(false);

  const getCategories = async () => {
    const res = await supabase.from('categories').select('*').order('id', { ascending: false });
    setCategories(res.data || []);
  };

  useEffect(() => {
    getCategories();
  }, []);

  const createCategory = async (e) => {
    e.preventDefault();

    await supabase.from('categories').insert([{
      name_en: name,
      color: color,
    }]);

    setName('');
    setColor('');
    setIsCreateOpen(false);
    await getCategories();
  };

  const editCategory = (category) => {
    setId(category.id);
    setEditName(category.name_en || '');
    setEditColor(category.color || '');
    setIsEditOpen(true);
  };

  const updateCategory = async (e) => {
    e.preventDefault();

    await supabase
      .from('categories')
      .update({
        name_en: editName,
        color: editColor,
      })
      .eq('id', id);

    setIsEditOpen(false);
    await getCategories();
  };

  const deleteCategory = async (categoryId) => {
    await supabase.from('categories').delete().eq('id', categoryId);
    await getCategories();
  };

  const filteredCategories = categories.filter((category) => {
    const value = search.toLowerCase();
    return (
      category.id.toString().includes(value) ||
      category.name_en?.toLowerCase().includes(value) ||
      category.color?.toLowerCase().includes(value)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filteredCategories.length / CATEGORIES_PER_PAGE));
  const startIndex = (currentPage - 1) * CATEGORIES_PER_PAGE;
  const paginatedCategories = filteredCategories.slice(startIndex, startIndex + CATEGORIES_PER_PAGE);

  return (
    <Layout>
      <div className="table_page">
        <Title subtitle="Manage categories" title="Categories" />

        <div className="table_controls_card">
          <label className="table_search" htmlFor="categories-search">
            <img alt="" src={searchIcon} />
            <input
              id="categories-search"
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Search categories..."
              type="text"
              value={search}
            />
          </label>

          <div className="table_controls_actions">
            <span>{filteredCategories.length} total records</span>
            <button className="table_primary_button" onClick={() => setIsCreateOpen(true)} type="button">
              Add Category
            </button>
          </div>
        </div>

        <div className="table_card">
          <div className="table_scroll">
            <table className="data_table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Color</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedCategories.map((category) => (
                  <tr key={category.id}>
                    <td>{category.id}</td>
                    <td>{category.name_en}</td>
                    <td>{category.color}</td>
                    <td>
                      <div className="table_actions">
                        <button
                          aria-label={`Edit category #${category.id}`}
                          className="table_icon_button"
                          onClick={() => editCategory(category)}
                          type="button"
                        >
                          <img alt="" src={editIcon} />
                        </button>
                        <button
                          aria-label={`Delete category #${category.id}`}
                          className="table_icon_button table_icon_button_danger"
                          onClick={() => deleteCategory(category.id)}
                          type="button"
                        >
                          <img alt="" src={deleteIcon} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="table_pagination">
            <button disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)} type="button">
              Previous
            </button>
            <span>Page {currentPage} of {totalPages}</span>
            <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)} type="button">
              Next
            </button>
          </div>
        </div>

        {isCreateOpen && (
          <div
            aria-labelledby="add-category-heading"
            aria-modal="true"
            className="table_modal_overlay"
            onClick={() => setIsCreateOpen(false)}
            role="dialog"
          >
            <div className="table_modal" onClick={(e) => e.stopPropagation()}>
              <div className="table_modal_header">
                <div>
                  <h2 id="add-category-heading">Add Category</h2>
                </div>
                <button
                  aria-label="Close add category form"
                  className="table_modal_close"
                  onClick={() => setIsCreateOpen(false)}
                  type="button"
                >
                  &#x2715;
                </button>
              </div>
              <form className="table_form" onSubmit={createCategory}>
                <div className="table_form_grid">
                  <label className="table_form_field">
                    <span>Name</span>
                    <input onChange={(e) => setName(e.target.value)} placeholder="Name" value={name} />
                  </label>
                  <label className="table_form_field">
                    <span>Color</span>
                    <input onChange={(e) => setColor(e.target.value)} placeholder="Color" value={color} />
                  </label>
                </div>
                <div className="table_form_actions">
                  <button className="table_secondary_button" onClick={() => setIsCreateOpen(false)} type="button">
                    Cancel
                  </button>
                  <button className="table_primary_button" type="submit">Create</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {isEditOpen && (
          <div
            aria-labelledby="edit-category-heading"
            aria-modal="true"
            className="table_modal_overlay"
            onClick={() => setIsEditOpen(false)}
            role="dialog"
          >
            <div className="table_modal" onClick={(e) => e.stopPropagation()}>
              <div className="table_modal_header">
                <div>
                  <h2 id="edit-category-heading">Edit Category</h2>
                </div>
                <button
                  aria-label="Close edit category form"
                  className="table_modal_close"
                  onClick={() => setIsEditOpen(false)}
                  type="button"
                >
                  &#x2715;
                </button>
              </div>
              <form className="table_form" onSubmit={updateCategory}>
                <div className="table_form_grid">
                  <label className="table_form_field">
                    <span>Name</span>
                    <input onChange={(e) => setEditName(e.target.value)} placeholder="Name" value={editName} />
                  </label>
                  <label className="table_form_field">
                    <span>Color</span>
                    <input onChange={(e) => setEditColor(e.target.value)} placeholder="Color" value={editColor} />
                  </label>
                </div>
                <div className="table_form_actions">
                  <button className="table_secondary_button" onClick={() => setIsEditOpen(false)} type="button">
                    Cancel
                  </button>
                  <button className="table_primary_button" type="submit">Save</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Categories;
